import React from 'react';
import { createPortal } from 'react-dom';

interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={() => onOpenChange(false)} />
      {children}
    </div>,
    document.body
  );
}

interface AlertDialogContentProps {
  className?: string;
  children: React.ReactNode;
}

export function AlertDialogContent({ className = '', children }: AlertDialogContentProps) {
  return (
    <div
      className={`fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 sm:rounded-lg ${className}`}
    >
      {children}
    </div>
  );
}

interface AlertDialogHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export function AlertDialogHeader({ className = '', children }: AlertDialogHeaderProps) {
  return (
    <div className={`flex flex-col space-y-2 text-center sm:text-left ${className}`}>
      {children}
    </div>
  );
}

interface AlertDialogTitleProps {
  className?: string;
  children: React.ReactNode;
}

export function AlertDialogTitle({ className = '', children }: AlertDialogTitleProps) {
  return <h2 className={`text-lg font-semibold ${className}`}>{children}</h2>;
}

interface AlertDialogDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

export function AlertDialogDescription({ className = '', children }: AlertDialogDescriptionProps) {
  return <div className={`text-sm text-gray-500 ${className}`}>{children}</div>;
}

interface AlertDialogFooterProps {
  className?: string;
  children: React.ReactNode;
}

export function AlertDialogFooter({ className = '', children }: AlertDialogFooterProps) {
  return (
    <div className={`flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 ${className}`}>
      {children}
    </div>
  );
}

interface AlertDialogCancelProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export function AlertDialogCancel({ className = '', children, onClick }: AlertDialogCancelProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mt-2 inline-flex h-10 items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold ring-offset-white transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 ${className}`}
    >
      {children}
    </button>
  );
}

interface AlertDialogActionProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export function AlertDialogAction({ className = '', children, onClick }: AlertDialogActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-10 items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white ring-offset-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}
